import { fetchPlantFlowHistory } from '@/api/plantFlowHistory';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  ArrowRight,
  Gauge,
  Clock3,
  Maximize2,
  Scan,
  Expand,
  Shrink,
  Minimize2,
  RefreshCw,
  Siren,
  PanelLeftClose,
  PanelLeftOpen,
  TriangleAlert,
  Zap,
  PanelTop,
  } from 'lucide-react';

import { fetchPowerPlants, type PowerPlant, fetchPowerPlantDetailSolarPanelSummary, type SolarPanelSummary } from '@/api/powerplants';
import { fetchDetailCards, type DetailCards } from '@/api/powerplantDetail';
import { fetchInverters, type Inverter } from '@/api/inverters';
import { fetchInverterDetail, type InverterDetail } from '@/api/inverterDetail';
import { useI18n } from '@/i18n';
import { useErrorToast, LoadRetry } from '@/lib/notify';

type FlowState = 'ok' | 'warn' | 'fault';
type LayoutMode = 'compact' | 'plant';

type ViewportState = {
  zoom: number;
  panX: number;
  panY: number;
};

type FlowString = {
  number: number;
  current: number | null;
  voltage: number | null;
  dcPower: number;
  efficiency: number | null;
  state: FlowState;
};

type FlowInverter = {
  id: number;
  uuid: string;
  name: string;
  brand: string;
  model: string;
  capacityKw: number | null;
  dailyEnergyKwh: number | null;
  hasAc: boolean;
  hasDc: boolean;
  acPower: number;
  dcPower: number;
  expected: number;
  state: FlowState;
  lastSeen: string | null;
  status: 0 | 1;
  strings: FlowString[];
};

type PlantFlow = {
  plant: PowerPlant;
  cards: DetailCards | null;
  panelSummary: SolarPanelSummary[];
  inverters: FlowInverter[];
  totalAc: number;
  totalDc: number;
  totalExpected: number;
  state: FlowState;
};

function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(/\s+/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePercent(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return value <= 1 ? value * 100 : value;
}

function stateTone(state: FlowState) {
  if (state === 'ok') {
    return { stroke: '#10b981', fill: '#ecfdf5', accent: '#059669', glow: 'rgba(16,185,129,0.42)' };
  }
  if (state === 'warn') {
    return { stroke: '#f59e0b', fill: '#fffbeb', accent: '#d97706', glow: 'rgba(245,158,11,0.5)' };
  }
  return { stroke: '#ef4444', fill: '#fef2f2', accent: '#dc2626', glow: 'rgba(239,68,68,0.58)' };
}

function ratioState(actual: number, expected: number, hardFault = false): FlowState {
  if (hardFault || !Number.isFinite(actual) || !Number.isFinite(expected) || expected <= 0) return 'fault';
  const ratio = actual / expected;
  if (ratio < 0.45) return 'fault';
  if (ratio < 0.82) return 'warn';
  return 'ok';
}

function stringState(current: number | null, voltage: number | null, efficiency: number | null): FlowState {
  if ((current === null || current <= 0) && (voltage === null || voltage <= 0)) return 'fault';
  if (current === null || current <= 0 || voltage === null || voltage <= 0) return 'fault';
  const eff = normalizePercent(efficiency);
  if (eff !== null && eff < 60) return 'fault';
  if (eff !== null && eff < 80) return 'warn';
  return 'ok';
}

function formatValue(value: number | null | undefined, unit: string, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: digits }).format(value)} ${unit}`;
}

function formatPercent(value: number | null | undefined, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `%${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: digits }).format(value)}`;
}

function MetricCard({
  label,
  value,
  subvalue,
  state,
  icon,
}: {
  label: string;
  value: string;
  subvalue: string;
  state: FlowState;
  icon: ReactNode;
}) {
  const tone = stateTone(state);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.2)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
          <div className="mt-1 text-xl font-black text-slate-900">{value}</div>
          <div className="mt-1 text-xs font-semibold text-slate-500">{subvalue}</div>
        </div>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{ background: tone.fill, color: tone.accent, boxShadow: `0 0 0 1px ${tone.stroke}22 inset` }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function TrendValue({ value, previous, unit, svg = false }: { value: number | null; previous: number | null | undefined; unit: string; svg?: boolean }) {
  const change = value != null && previous != null && Number.isFinite(value) && Number.isFinite(previous)
    ? Math.abs(value - previous) < 0.000001 ? 0 : value > previous ? 1 : -1 : 0;
  const color = change > 0 ? '#16a34a' : change < 0 ? '#dc2626' : undefined;
  const text = `${value == null ? '—' : formatValue(value, unit)}${change > 0 ? ' ↑' : change < 0 ? ' ↓' : ''}`;
  return svg ? <tspan style={{ fill: color }}>{text}</tspan> : <span style={{ color }}>{text}</span>;
}

export default function EnergyFlowPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { uuid_id } = useParams<{ uuid_id?: string }>();
  const location = useLocation();
  const queryPlant = useMemo(() => new URLSearchParams(location.search).get('plant'), [location.search]);
  const routePlantId = uuid_id ?? queryPlant ?? null;
  const [plants, setPlants] = useState<PowerPlant[]>([]);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(routePlantId);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('compact');
  const [showSidebar, setShowSidebar] = useState(false);
  const [loadingFlow, setLoadingFlow] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [flowMode, setFlowMode] = useState<'live' | 'history'>('live');
  const [historyDate, setHistoryDate] = useState(() => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Istanbul' }).format(new Date()));
  const [historyTime, setHistoryTime] = useState(() => new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date()));
  const [historyRecords, setHistoryRecords] = useState<string[]>([]);
  const [recordAt, setRecordAt] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playbackDelay, setPlaybackDelay] = useState(2000);

  const [error, setError] = useState('');
  useErrorToast(error);
  const [flow, setFlow] = useState<PlantFlow | null>(null);
  const flowRef = useRef<PlantFlow | null>(null);
  const [previousFlow, setPreviousFlow] = useState<PlantFlow | null>(null);
  const previousInverters = useMemo(() => new Map(previousFlow?.inverters.map(item => [item.id, item]) ?? []), [previousFlow]);
  function previousString(id: number, number: number) {
    return previousInverters.get(id)?.strings.find(item => item.number === number);
  }
  function stringReading(id: number, string: FlowString, field: 'current' | 'voltage' | 'dcPower', unit: string, svg = false) {
    const before = previousString(id, string.number);
    const value = field === 'dcPower' && (string.current == null || string.voltage == null) ? null : string[field];
    const previous = field === 'dcPower' && (before?.current == null || before?.voltage == null) ? null : before?.[field];
    return <TrendValue value={value} previous={previous} unit={unit} svg={svg} />;
  }

  const [isDragging, setIsDragging] = useState(false);
  const [fitToWindow, setFitToWindow] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const fullscreenRef = useRef<HTMLDivElement | null>(null);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  const [fallbackFullscreen, setFallbackFullscreen] = useState(false);
  const fullscreen = nativeFullscreen || fallbackFullscreen;
  useEffect(() => {
    const target = fullscreenRef.current;
    const sync = () => setNativeFullscreen(!!target && document.fullscreenElement === target);
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setFallbackFullscreen(false); };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('keydown', escape);
      if (target && document.fullscreenElement === target) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);
  useEffect(() => {
    if (!fallbackFullscreen) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [fallbackFullscreen]);
  async function toggleFullscreen() {
    if (fallbackFullscreen) { setFallbackFullscreen(false); return; }
    if (document.fullscreenElement === fullscreenRef.current) { await document.exitFullscreen().catch(() => undefined); return; }
    try {
      if (!fullscreenRef.current?.requestFullscreen || !document.fullscreenEnabled) { setFallbackFullscreen(true); return; }
      await fullscreenRef.current.requestFullscreen();
    } catch { setFallbackFullscreen(true); }
  }

  const dragRef = useRef<{
    active: boolean;
    pointerId: number | null;
    startClientX: number;
    startClientY: number;
    startPanX: number;
    startPanY: number;
    moved: boolean;
  }>({
    active: false,
    pointerId: null,
    startClientX: 0,
    startClientY: 0,
    startPanX: 0,
    startPanY: 0,
    moved: false,
  });
  const suppressClickRef = useRef(false);
  const [viewport, setViewport] = useState<ViewportState>({ zoom: 1, panX: 0, panY: 0 });

  useEffect(() => {
    let cancelled = false;
    fetchPowerPlants()
      .then((items) => {
        if (cancelled) return;
        setPlants(items);
        if (!routePlantId && !selectedUuid && items[0]?.power_plant_uuid_id) {
          setSelectedUuid(items[0].power_plant_uuid_id);
        } else if ((routePlantId || selectedUuid) && !items.some((plant) => plant.power_plant_uuid_id === (routePlantId ?? selectedUuid)) && items[0]?.power_plant_uuid_id) {
          setSelectedUuid(items[0].power_plant_uuid_id);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('powerPlants.loadError'));
      });

    return () => {
      cancelled = true;
    };
  }, [routePlantId, selectedUuid, t]);

  useEffect(() => {
    if (routePlantId) setSelectedUuid(routePlantId);
  }, [routePlantId]);

  useEffect(() => {
    flowRef.current = null;
    setFlow(null);
    setPreviousFlow(null);
    setRecordAt(null);
  }, [routePlantId, selectedUuid, flowMode, historyDate]);

  useEffect(() => {
    const activeUuid = routePlantId ?? selectedUuid;
    if (!activeUuid) return;
    let cancelled = false;
    setLoadingFlow(true);
    setError('');

    (async () => {
      try {
        const history = flowMode === 'history' ? await fetchPlantFlowHistory(activeUuid, historyDate, historyTime) : null;
        if (cancelled) return;
        if (history) {
          if (!history.at) { setHistoryRecords([]); setRecordAt(null); setFlow(null); flowRef.current = null; setPreviousFlow(null); setError('Bu tarihte kayıt bulunamadı.'); return; }
        }
        const [cards, panelSummary, inverterList] = await Promise.all([
          history ? Promise.resolve(null) : fetchDetailCards(activeUuid),
          fetchPowerPlantDetailSolarPanelSummary(activeUuid),
          history ? Promise.resolve(history.inverters.map(item => ({ id: item.id, inverter_name: item.inverter_name } as Inverter))) : fetchInverters(activeUuid),
        ]);

        const inverterDetails = await Promise.allSettled(
          inverterList.map(async (inverter: Inverter) => {
            const detail = history ? history.inverters.find(item => item.id === inverter.id)!.detail : await fetchInverterDetail(inverter.id, { view: 'flow' });
            return { inverter, detail };
          }),
        );

        const detailedInverters: FlowInverter[] = inverterDetails
          .filter((entry): entry is PromiseFulfilledResult<{ inverter: Inverter; detail: InverterDetail }> => entry.status === 'fulfilled')
          .map((entry) => {
            const { inverter, detail } = entry.value;
            const strings: FlowString[] = detail.strings.map((stringRow) => {
              const currents = [stringRow.string_current_ftp, stringRow.string_current_modbus].filter((value): value is number => value != null).map(Number);
              const current = currents.length ? Math.max(...currents) : null;
              const voltages = [stringRow.string_voltage_ftp, stringRow.string_voltage_modbus].filter((value): value is number => value != null).map(Number);
              const voltage = voltages.length ? Math.max(...voltages) : null;
              const dcPower = current !== null && voltage !== null ? (current * voltage) / 1000 : 0;
              const efficiency = stringRow.string_efficiency ?? null;
              return {
                number: stringRow.string_number,
                current,
                voltage,
                dcPower,
                efficiency,
                state: stringState(current, voltage, efficiency),
              };
            });

            const acPower = detail.current.inverter_active_power ?? 0;
            const dcPower = strings.reduce((sum, item) => sum + item.dcPower, 0);
            const capacityKw = parseNumber(detail.current.inverter_capacity_kwp);
            const expected = capacityKw ?? Math.max(dcPower, acPower * 1.12);
            const hardFault = detail.current.inverter_status === 0
              || (detail.current.inverter_ftp_connection_status === 0 && detail.current.inverter_modbus_connection_status === 0);
            const stringFaults = strings.filter((item) => item.state === 'fault').length;
            const inverterState = history && !detail.current.inverter_last_seen ? 'warn' : hardFault
              ? 'fault'
              : stringFaults > 0
                ? 'warn'
                : ratioState(acPower, expected, false);

            return {
              id: inverter.id,
              uuid: detail.current.inverter_uuid_id,
              name: inverter.inverter_name,
              brand: detail.current.inverter_brand,
              model: detail.current.inverter_model,
              capacityKw,
              dailyEnergyKwh: detail.current.inverter_e_day_kwh == null ? null : Number(detail.current.inverter_e_day_kwh),
              hasAc: detail.current.inverter_active_power != null,
              hasDc: strings.length > 0 && strings.every(item => item.current != null && item.voltage != null),
              acPower,
              dcPower,
              expected,
              state: inverterState,
              lastSeen: detail.current.inverter_last_seen,
              status: detail.current.inverter_status,
              strings,
            };
          });

        const totalAc = detailedInverters.reduce((sum, inverter) => sum + inverter.acPower, 0);
        const totalDc = detailedInverters.reduce((sum, inverter) => sum + inverter.dcPower, 0);
        const totalExpected = detailedInverters.reduce((sum, inverter) => sum + inverter.expected, 0);
        const faultCount = detailedInverters.filter((inverter) => inverter.state === 'fault').length;
        const warnCount = detailedInverters.filter((inverter) => inverter.state === 'warn').length;
        const plantState = faultCount > 0 ? 'fault' : warnCount > 0 ? 'warn' : ratioState(totalAc, totalExpected, false);
        const selectedPlant = plants.find((plant) => plant.power_plant_uuid_id === activeUuid) ?? null;

        if (!cancelled && selectedPlant) {
          const nextFlow: PlantFlow = {
            plant: selectedPlant,
            cards,
            panelSummary,
            inverters: detailedInverters,
            totalAc,
            totalDc,
            totalExpected,
            state: plantState,
          };
          setPreviousFlow(flowRef.current);
          flowRef.current = nextFlow;
          setFlow(nextFlow);
          if (history) { setHistoryRecords(history.records); setRecordAt(history.at); }
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('powerPlants.loadError'));
        }
      } finally {
        if (!cancelled) setLoadingFlow(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routePlantId, selectedUuid, refreshTick, plants, t, flowMode, historyDate, historyTime]);

  useEffect(() => {
    setPlaying(false);
  }, [flowMode, historyDate, routePlantId, selectedUuid]);

  useEffect(() => {
    if (!playing || flowMode !== 'history' || loadingFlow) return;
    const index = recordAt ? historyRecords.indexOf(recordAt) : -1;
    if (error || !flow || index < 0 || index >= historyRecords.length - 1) {
      setPlaying(false);
      return;
    }
    // Wait for this record to finish loading before scheduling the next one.
    const timer = window.setTimeout(() => {
      setHistoryTime(historyRecords[index + 1].slice(11));
    }, playbackDelay);
    return () => window.clearTimeout(timer);
  }, [playing, flowMode, loadingFlow, recordAt, historyRecords, playbackDelay, error, flow]);

  useEffect(() => {
    if (!autoRefreshEnabled || flowMode === 'history') return;
    const intervalId = window.setInterval(() => {
      if (!loadingFlow) {
        setRefreshTick((prev) => prev + 1);
      }
    }, 30_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [autoRefreshEnabled, loadingFlow, flowMode]);

  const layout = useMemo(() => {
    const inverters = flow?.inverters ?? [];
    const inverterCols = Math.max(1, inverters.length);
    const inverterWidth = layoutMode === 'plant' ? 330 : 300;
    const inverterGapX = layoutMode === 'plant' ? 28 : 24;
    const stringCols = layoutMode === 'plant' ? 4 : 3;
    const stringWidth = layoutMode === 'plant' ? 68 : 72;
    const stringHeight = layoutMode === 'plant' ? 74 : 78;
    const stringGapX = 8;
    const stringGapY = 10;
    const inverterInnerTop = 142;
    const basePlantHeight = 162;
    const basePlantWidth = layoutMode === 'plant' ? 480 : 430;
    const baseStartY = basePlantHeight + 72;
    const stageWidth = Math.max(1360, inverterCols * inverterWidth + (inverterCols - 1) * inverterGapX + 180);

    const groups = inverters.map((inverter, index) => {
      const stringRows = Math.max(1, Math.ceil(inverter.strings.length / stringCols));
      const groupHeight = inverterInnerTop + stringRows * (stringHeight + stringGapY) + 26;
      return {
        ...inverter,
        index,
        row: Math.floor(index / inverterCols),
        col: index % inverterCols,
        stringRows,
        groupHeight,
      };
    });

    const rowHeights: number[] = [];
    groups.forEach((group) => {
      rowHeights[group.row] = Math.max(rowHeights[group.row] ?? 0, group.groupHeight);
    });

    const rowYStarts: number[] = [];
    let currentY = baseStartY;
    rowHeights.forEach((rowHeight, row) => {
      rowYStarts[row] = currentY;
        currentY += rowHeight + (layoutMode === 'plant' ? 36 : 32);
      });

    const gridWidth = inverterCols * inverterWidth + (inverterCols - 1) * inverterGapX;
    const gridStartX = Math.max(68, Math.floor((stageWidth - gridWidth) / 2));
    const stageHeight = Math.max(currentY + 110, baseStartY + 280);

    return {
      inverters: groups,
      rowHeights,
      rowYStarts,
      gridStartX,
      stageWidth,
      stageHeight,
      inverterWidth,
      inverterGapX,
      stringCols,
      stringWidth,
      stringHeight,
      stringGapX,
      stringGapY,
      inverterInnerTop,
      basePlantWidth,
      basePlantHeight,
    };
  }, [flow?.inverters, layoutMode]);

  const clamp = useCallback((value: number, min: number, max: number) => Math.min(max, Math.max(min, value)), []);

  const resetViewport = useCallback(() => {
    setFitToWindow(false);
    setViewport({ zoom: 1, panX: 0, panY: 0 });
    requestAnimationFrame(() => {
      const canvas = svgRef.current?.parentElement;
      if (canvas) canvas.scrollTo({ left: Math.max(0, (canvas.scrollWidth - canvas.clientWidth) / 2), top: 0 });
    });
  }, []);

  const zoomToPoint = useCallback(
    (nextZoom: number, clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const next = clamp(nextZoom, 0.55, 2.4);
      const pointX = ((clientX - rect.left) / rect.width) * layout.stageWidth;
      const pointY = ((clientY - rect.top) / rect.height) * layout.stageHeight;

      setViewport((current) => ({
        zoom: next,
        panX: current.panX + pointX * (current.zoom - next),
        panY: current.panY + pointY * (current.zoom - next),
      }));
    },
    [clamp, layout.stageWidth, layout.stageHeight],
  );

  const handleWheel = useCallback(
    (event: React.WheelEvent<SVGSVGElement>) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.12 : 0.12;
      zoomToPoint(viewport.zoom + delta, event.clientX, event.clientY);
    },
    [viewport.zoom, zoomToPoint],
  );

  const handlePointerDown = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const svg = svgRef.current;
    if (!svg) return;
    if ((event.target as HTMLElement)?.closest('[data-flow-interactive="true"]')) return;

    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPanX: viewport.panX,
      startPanY: viewport.panY,
      moved: false,
    };
    setIsDragging(false);
    svg.setPointerCapture(event.pointerId);
  }, [viewport.panX, viewport.panY]);

  const handlePointerMove = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startClientX;
    const dy = event.clientY - drag.startClientY;
    if (!drag.moved && Math.hypot(dx, dy) > 4) {
      drag.moved = true;
      suppressClickRef.current = true;
      setIsDragging(true);
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }

    if (!drag.moved) return;

    const scale = viewport.zoom || 1;
    setViewport({
      zoom: viewport.zoom,
      panX: drag.startPanX + dx / scale,
      panY: drag.startPanY + dy / scale,
    });
  }, [viewport.zoom]);

  const endDrag = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;
    drag.active = false;
    drag.pointerId = null;
    setIsDragging(false);
    const svg = svgRef.current;
    if (svg && svg.hasPointerCapture(event.pointerId)) {
      svg.releasePointerCapture(event.pointerId);
    }
  }, []);

  const hasFlow = flow !== null;
  useEffect(() => {
    if (!hasFlow) return;
    const frame = requestAnimationFrame(resetViewport);
    return () => cancelAnimationFrame(frame);
  }, [layoutMode, resetViewport, routePlantId, selectedUuid, hasFlow]);

  const handleClickCapture = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const warningItems = useMemo(() => {
    if (!flow) return [];
    return flow.inverters
      .flatMap((inverter) => inverter.strings.map((stringNode) => ({
        inverter,
        stringNode,
      })))
      .filter(({ inverter, stringNode }) => inverter.state !== 'ok' || stringNode.state !== 'ok')
      .slice(0, 8);
  }, [flow]);

  const pagePlantName = flow?.plant.power_plant_name
    ?? plants.find((plant) => plant.power_plant_uuid_id === (routePlantId ?? selectedUuid))?.power_plant_name
    ?? 'Santral Akış Ekranı';

  if (error) {
    return <LoadRetry />;
  }

  return (
    <div ref={fullscreenRef} style={fallbackFullscreen ? { position: 'fixed', inset: 0, zIndex: 100, width: '100vw', height: '100dvh' } : undefined} className={`flow-fullscreen flex min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.18),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.12),_transparent_28%),linear-gradient(180deg,#f8fbff_0%,#eef4ff_45%,#f8fafc_100%)] p-4 md:p-6 ${embedded ? 'h-[850px] w-full rounded-2xl border border-slate-200' : 'h-full'}`}>
      <style>{`
        .flow-fullscreen:fullscreen { width: 100vw; height: 100dvh; box-sizing: border-box; }

        .float-node {
          animation: node-float 5.5s ease-in-out infinite;
        }
        .float-node-delay {
          animation: node-float 6.4s ease-in-out infinite;
        }
        .svg-title {
          font: 800 18px Inter, system-ui, sans-serif;
          fill: #0f172a;
        }
        .svg-subtitle {
          font: 600 11px Inter, system-ui, sans-serif;
          fill: #64748b;
        }
        .svg-details {
          font: 700 10px Inter, system-ui, sans-serif;
          fill: #334155;
        }
        .svg-label-top {
          font: 800 9px Inter, system-ui, sans-serif;
          fill: #94a3b8;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }
        @keyframes node-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
      `}</style>

      <div className="mx-auto flex h-full min-h-0 w-full max-w-[1700px] flex-col gap-4">
        <div className="shrink-0 rounded-[28px] border border-slate-200 bg-white/90 px-4 py-3 shadow-[0_18px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {!embedded && <div className="flex items-center gap-3">

              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400">Santral Akış Ekranı</div>
                <h1 className="mt-1 text-lg font-black text-slate-900 sm:text-xl">{pagePlantName}</h1>
              </div>
            </div>}
            <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
              <select aria-label="Akış zamanı" value={flowMode} onChange={event => { setFlowMode(event.target.value as 'live' | 'history'); setHistoryRecords([]); setRecordAt(null); }} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold">
                <option value="live">Canlı</option><option value="history">Geçmiş</option>
              </select>
              {flowMode === 'history' && <>
                <input type="date" aria-label="Kayıt tarihi" value={historyDate} onChange={event => { if (event.target.value) { setPlaying(false); setHistoryDate(event.target.value); setHistoryRecords([]); } }} className="rounded-xl border border-slate-200 px-2 py-2 text-xs" />
                <input type="time" step="1" aria-label="Kayıt saati" value={historyTime} onChange={event => { if (event.target.value) { setPlaying(false); setHistoryTime(event.target.value); } }} className="rounded-xl border border-slate-200 px-2 py-2 text-xs" />
                <button type="button" aria-label="Önceki kayıt" title="Önceki veri kaydı" disabled={loadingFlow || !recordAt || historyRecords.indexOf(recordAt) <= 0} onClick={() => { setPlaying(false); const index = historyRecords.indexOf(recordAt!); if (index > 0) setHistoryTime(historyRecords[index - 1].slice(11)); }} className="rounded-xl border border-slate-200 p-2 disabled:opacity-30"><ChevronLeft size={16} /></button>
                <button type="button" aria-label="Sonraki kayıt" title="Sonraki veri kaydı" disabled={loadingFlow || !recordAt || historyRecords.indexOf(recordAt) >= historyRecords.length - 1} onClick={() => { setPlaying(false); const index = historyRecords.indexOf(recordAt!); if (index >= 0 && index < historyRecords.length - 1) setHistoryTime(historyRecords[index + 1].slice(11)); }} className="rounded-xl border border-slate-200 p-2 disabled:opacity-30"><ChevronRight size={16} /></button>
                <button type="button" aria-label={playing ? 'Oynatmayı duraklat' : 'Geçmiş kayıtları oynat'} aria-pressed={playing}
                  disabled={!playing && (loadingFlow || !recordAt || !!error || historyRecords.indexOf(recordAt) >= historyRecords.length - 1)}
                  onClick={() => setPlaying(previous => !previous)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold disabled:opacity-30">
                  {playing ? <Pause size={16} /> : <Play size={16} />}{playing ? 'Duraklat' : 'Oynat'}
                </button>
                <label className="flex items-center gap-1.5 text-xs text-slate-500">Hız
                  <select aria-label="Geçmiş oynatma hızı" value={playbackDelay} onChange={event => setPlaybackDelay(Number(event.target.value))} className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-xs font-bold text-slate-700">
                    <option value={4000}>0,5× · 4 sn/kayıt</option>
                    <option value={2000}>1× · 2 sn/kayıt</option>
                    <option value={1000}>2× · 1 sn/kayıt</option>
                    <option value={500}>4× · 0,5 sn/kayıt</option>
                  </select>
                </label>
              </>}

              <button
                type="button"
                onClick={() => setRefreshTick((prev) => prev + 1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingFlow ? 'animate-spin' : ''}`} />
                Yenile
              </button>
              <button
                type="button"
                disabled={flowMode === 'history'}
                onClick={() => setAutoRefreshEnabled((prev) => !prev)}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-bold transition ${
                  autoRefreshEnabled
                    ? 'border border-emerald-400/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                title={autoRefreshEnabled ? 'Auto refresh açık' : 'Auto refresh kapalı'}
                aria-label={autoRefreshEnabled ? 'Auto refresh açık' : 'Auto refresh kapalı'}
              >
                <Clock3 className="h-4 w-4" />
                {autoRefreshEnabled ? 'Auto 30s' : 'Auto Refresh'}
              </button>
              <button
                type="button"
                onClick={() => setLayoutMode((prev) => (prev === 'compact' ? 'plant' : 'compact'))}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                {layoutMode === 'compact' ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
                {layoutMode === 'compact' ? 'Büyük Santral Modu' : 'Kompakt Mod'}
              </button>
              <button
                type="button"
                onClick={() => setShowSidebar((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                {showSidebar ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>

        <div
          className={`grid min-h-0 flex-1 gap-4 transition-[grid-template-columns] duration-500 ease-out ${
            showSidebar ? 'xl:grid-cols-[minmax(0,1.9fr)_minmax(340px,0.95fr)]' : 'xl:grid-cols-1'
          }`}
        >
          <div className="relative min-h-0 min-w-0 rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(15,23,42,0.88))] p-3 shadow-[0_32px_100px_-50px_rgba(15,23,42,0.85)] transition-all duration-500 ease-out">
              <div className="pointer-events-none absolute bottom-3 left-3 z-20 flex items-center gap-2 rounded-xl border border-white/10 bg-slate-950/70 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-[0.18em] text-white/85 backdrop-blur">
                <span>Zoom {Math.round(viewport.zoom * 100)}%</span>
                <span className="text-white/40">•</span>
                <span>Ctrl + wheel</span>
                <span className="text-white/40">•</span>
                <span>Sürükle / yatay kaydır</span>
              </div>
              <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
                <button type="button" onClick={() => void toggleFullscreen()} title={fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'} aria-label={fullscreen ? 'Tam ekrandan çık' : 'Akışı tam ekran aç'} aria-pressed={fullscreen}
                  className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 bg-slate-950/70 px-2.5 text-[10px] font-bold text-white backdrop-blur hover:bg-slate-900/90">
                  {fullscreen ? <Shrink size={15} /> : <Expand size={15} />}{fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
                </button>
                <button type="button" onClick={() => {
                  setFitToWindow(true);
                  setViewport({ zoom: 1, panX: 0, panY: 0 });
                  svgRef.current?.parentElement?.scrollTo({ left: 0, top: 0 });
                }} title="Tüm akışı ekrana sığdır" aria-label="Akışı ekrana sığdır" aria-pressed={fitToWindow}
                  className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 bg-slate-950/70 px-2.5 text-[10px] font-bold text-white backdrop-blur hover:bg-slate-900/90">
                  <Scan size={15} />Ekrana sığdır
                </button>
                <button
                  type="button"
                  onClick={() => zoomToPoint(viewport.zoom + 0.15, layout.stageWidth / 2, layout.stageHeight / 2)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-slate-950/70 text-white backdrop-blur transition hover:bg-slate-900/90"
                  aria-label="Yakınlaştır"
                  title="Yakınlaştır"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => zoomToPoint(viewport.zoom - 0.15, layout.stageWidth / 2, layout.stageHeight / 2)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-slate-950/70 text-white backdrop-blur transition hover:bg-slate-900/90"
                  aria-label="Uzaklaştır"
                  title="Uzaklaştır"
                >
                  -
                </button>
                <button
                  type="button"
                  onClick={resetViewport}
                  className="inline-flex h-9 items-center justify-center rounded-xl border border-white/10 bg-slate-950/70 px-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-white backdrop-blur transition hover:bg-slate-900/90"
                  aria-label="Sıfırla"
                  title="Sıfırla"
                >
                  Sıfırla
                </button>
              </div>
            <div className="relative h-full min-h-0 overflow-auto rounded-[24px] border border-white/10 bg-[radial-gradient(circle_at_center,_rgba(14,165,233,0.10),_transparent_42%),linear-gradient(180deg,rgba(15,23,42,0.92),rgba(15,23,42,0.82))] transition-all duration-500 ease-out">
              {!flow ? (
                <div className="flex min-h-[640px] items-center justify-center px-6 text-sm font-semibold text-slate-300">
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                    <RefreshCw className={`h-3.5 w-3.5 text-sky-300 ${loadingFlow ? 'animate-spin' : ''}`} />
                    {loadingFlow ? (flowMode === 'history' ? 'Geçmiş kayıt hazırlanıyor...' : 'Canlı veri hazırlanıyor...') : (error || 'Gösterilecek veri yok.')}
                  </div>
                </div>
              ) : (
                <svg
                  ref={svgRef}
                  viewBox={`0 0 ${layout.stageWidth} ${layout.stageHeight}`}
                  className="h-auto w-full select-none"
                  style={{
                    minWidth: fitToWindow ? undefined : `${layout.stageWidth}px`,
                    height: fitToWindow ? '100%' : undefined,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                  }}
                  onWheel={handleWheel}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  onPointerLeave={endDrag}
                  onClickCapture={handleClickCapture}
                >
                  <defs>
                    <linearGradient id="plantGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.15" />
                      <stop offset="50%" stopColor="#34d399" stopOpacity="0.26" />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.16" />
                    </linearGradient>
                    <filter id="shadowGlow" x="-30%" y="-30%" width="160%" height="160%">
                      <feDropShadow dx="0" dy="16" stdDeviation="18" floodColor="rgba(15,23,42,0.55)" />
                    </filter>
                  </defs>

                  <rect x="18" y="18" width={layout.stageWidth - 36} height={layout.stageHeight - 36} rx="28" fill="url(#plantGlow)" opacity="0.95" />

                  {flow && (
                    <g transform={`matrix(${viewport.zoom} 0 0 ${viewport.zoom} ${viewport.panX} ${viewport.panY})`}>
                      <g
                        className="cursor-pointer"
                        data-flow-interactive="true"
                        onClick={() => navigate(`/dashboard/powerplants/${flow.plant.power_plant_uuid_id}`)}
                      >
                        <rect
                          x={(layout.stageWidth - layout.basePlantWidth) / 2}
                          y={48}
                          width={layout.basePlantWidth}
                          height={layout.basePlantHeight}
                          rx="24"
                          fill="rgba(255,255,255,0.90)"
                          stroke="#dbeafe"
                          strokeWidth="1.5"
                        />
                        <rect
                          x={(layout.stageWidth - layout.basePlantWidth) / 2 + 12}
                          y={60}
                          width={layout.basePlantWidth - 24}
                          height="6"
                          rx="3"
                          fill={stateTone(flow.state).stroke}
                        />
                        <text
                          x={layout.stageWidth / 2}
                          y={88}
                          textAnchor="middle"
                          className="svg-label-top"
                          fill="#94a3b8"
                        >
                          SANTRAL SEVİYESİ
                        </text>
                        <text
                          x={layout.stageWidth / 2}
                          y={118}
                          textAnchor="middle"
                          className="svg-title"
                          style={{ fontSize: layoutMode === 'plant' ? '20px' : '19px' }}
                        >
                          {flow.plant.power_plant_name}
                        </text>
                        <text
                          x={layout.stageWidth / 2}
                          y={140}
                          textAnchor="middle"
                          className="svg-subtitle"
                          style={{ fill: '#475569' }}
                        >
                          {[flow.cards?.plantTown ?? flow.plant.power_plant_town, flow.cards?.plantCity ?? flow.plant.power_plant_city].filter(Boolean).join(' · ')}
                        </text>
                        <text
                          x={layout.stageWidth / 2}
                          y={160}
                          textAnchor="middle"
                          className="svg-details"
                        >
                          <TrendValue value={flow.inverters.every(item => item.hasAc) ? flow.totalAc : null} previous={previousFlow?.inverters.every(item => item.hasAc) ? previousFlow.totalAc : null} unit="kW" svg /> AC · <TrendValue value={flow.inverters.every(item => item.hasDc) ? flow.totalDc : null} previous={previousFlow?.inverters.every(item => item.hasDc) ? previousFlow.totalDc : null} unit="kW" svg /> DC · Beklenen {formatValue(flow.totalExpected, 'kW')}
                        </text>
                        <text x={layout.stageWidth / 2} y={187} textAnchor="middle" className="svg-title" style={{ fontSize: '16px' }}>
                          {flowMode === 'history' ? 'O ana kadar üretim' : 'Günlük üretim'}: {flow.inverters.length > 0 && flow.inverters.every(item => item.dailyEnergyKwh != null)
                            ? formatValue(flow.inverters.reduce((sum, item) => sum + item.dailyEnergyKwh!, 0), 'kWh', 2) : '—'}
                        </text>
                        <circle
                          cx={layout.stageWidth / 2 + layout.basePlantWidth / 2 - 28}
                          cy={90}
                          r="5"
                          fill={stateTone(flow.state).stroke}
                        />
                        <circle
                          cx={layout.stageWidth / 2 + layout.basePlantWidth / 2 - 28}
                          cy={90}
                          r="11"
                          fill="none"
                          stroke={stateTone(flow.state).stroke}
                          strokeOpacity="0.18"
                          strokeWidth="2"
                        />
                      </g>

                      {layout.inverters.map((group) => {
                        const x = layout.gridStartX + group.col * (layout.inverterWidth + layout.inverterGapX);
                        const y = layout.rowYStarts[group.row];
                        const inverterTopY = y;
                        const stringStartY = y + layout.inverterInnerTop;
                        const stringsPerRow = layoutMode === 'plant' ? 4 : 3;

                        return (
                          <g key={group.id} filter="url(#shadowGlow)">
                            <title>{`${group.name} · Kayıt: ${group.lastSeen || 'Veri yok'}`}</title>
                            <g
                              className="cursor-pointer"
                              data-flow-interactive="true"
                              onClick={() => navigate(`/dashboard/inverters/${group.id}`)}
                            >
                              <rect x={x} y={inverterTopY} width={layout.inverterWidth} height="132" rx="20" fill="rgba(255,255,255,0.92)" stroke={stateTone(group.state).stroke} />
                              <rect x={x + 12} y={inverterTopY + 12} width={layout.inverterWidth - 24} height="5" rx="2.5" fill={stateTone(group.state).stroke} />
                              <text x={x + 16} y={inverterTopY + 36} className="svg-label-top">{group.name}</text>
                              <text x={x + 16} y={inverterTopY + 57} className="svg-title" style={{ fontSize: '15px' }}>{group.brand}</text>
                              <text x={x + 16} y={inverterTopY + 74} className="svg-subtitle">{group.model}</text>
                              <text x={x + 16} y={inverterTopY + 95} className="svg-details">
                                <TrendValue value={group.hasAc ? group.acPower : null} previous={previousInverters.get(group.id)?.hasAc ? previousInverters.get(group.id)?.acPower : null} unit="kW" svg /> AC · <TrendValue value={group.hasDc ? group.dcPower : null} previous={previousInverters.get(group.id)?.hasDc ? previousInverters.get(group.id)?.dcPower : null} unit="kW" svg /> DC
                              </text>
                              <text x={x + 16} y={inverterTopY + 112} className="svg-details">
                                {flowMode === 'history' ? 'O ana kadar' : 'Günlük üretim'}: {group.dailyEnergyKwh == null ? '—' : formatValue(group.dailyEnergyKwh, 'kWh', 2)}
                              </text>
                              <circle cx={x + layout.inverterWidth - 24} cy={inverterTopY + 28} r="5" fill={stateTone(group.state).stroke} />
                              <circle cx={x + layout.inverterWidth - 24} cy={inverterTopY + 28} r="11" fill="none" stroke={stateTone(group.state).stroke} strokeOpacity="0.18" strokeWidth="2" />
                            </g>

                            {group.strings.map((stringNode, index) => {
                              const row = Math.floor(index / stringsPerRow);
                              const col = index % stringsPerRow;
                              const stringX = x + 16 + col * (layout.stringWidth + layout.stringGapX);
                              const stringY = stringStartY + row * (layout.stringHeight + layout.stringGapY);
                              const tone = stateTone(stringNode.state);
                              return (
                                <g
                                  key={stringNode.number}
                                  className={`cursor-pointer ${stringNode.state === 'ok' ? 'float-node' : 'float-node-delay'}`}
                                  data-flow-interactive="true"
                                  onClick={() => navigate(`/dashboard/inverters/${group.id}/string/${stringNode.number}`)}
                                >
                                  <rect x={stringX} y={stringY} width={layout.stringWidth} height={layout.stringHeight} rx="14" fill="rgba(255,255,255,0.88)" stroke={tone.stroke} />
                                  <rect x={stringX + 6} y={stringY + 6} width={layout.stringWidth - 12} height="4" rx="2" fill={tone.stroke} />
                                  <text x={stringX + layout.stringWidth / 2} y={stringY + 24} textAnchor="middle" className="svg-label-top">{`S-${String(stringNode.number).padStart(2, '0')}`}</text>
                                  <text x={stringX + layout.stringWidth / 2} y={stringY + 43} textAnchor="middle" className="svg-title" style={{ fontSize: '14px' }}>
                                    {stringReading(group.id, stringNode, 'current', 'A', true)}
                                  </text>
                                  <text x={stringX + layout.stringWidth / 2} y={stringY + 56} textAnchor="middle" className="svg-subtitle">
                                    {stringReading(group.id, stringNode, 'voltage', 'V', true)}
                                  </text>
                                  <text x={stringX + layout.stringWidth / 2} y={stringY + 70} textAnchor="middle" className="svg-details">
                                    {stringReading(group.id, stringNode, 'dcPower', 'kW', true)}
                                  </text>
                                  <circle cx={stringX + layout.stringWidth - 13} cy={stringY + 18} r="4.5" fill={tone.stroke} />
                                </g>
                              );
                            })}
                          </g>
                        );
                      })}
                    </g>
                  )}
                </svg>
              )}
            </div>
          </div>

          {showSidebar ? (
          <aside className="min-h-0 space-y-4 overflow-y-auto transition-all duration-500 ease-out opacity-100 translate-x-0">
            <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_80px_-44px_rgba(15,23,42,0.35)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400">Özet Panel</div>
                  <h2 className="mt-1 text-xl font-black text-slate-900">Anlık Durum</h2>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5 text-slate-700">
                  <Gauge className="h-4 w-4" />
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2.5">
                <MetricCard
                  label="String"
                  value={`${flow?.inverters.reduce((sum, inverter) => sum + inverter.strings.length, 0) ?? 0}`}
                  subvalue={`${warningItems.filter((item) => item.stringNode.state !== 'ok').length} sapmalı string`}
                  state={warningItems.some((item) => item.stringNode.state === 'fault') ? 'fault' : warningItems.some((item) => item.stringNode.state === 'warn') ? 'warn' : 'ok'}
                  icon={<PanelTop className="h-5 w-5" />}
                />
                <MetricCard
                  label="Inverter"
                  value={`${flow?.inverters.length ?? 0}`}
                  subvalue={`${flow?.inverters.filter((inverter) => inverter.state !== 'ok').length ?? 0} sorunlu inverter`}
                  state={flow?.state ?? 'warn'}
                  icon={<ArrowRight className="h-5 w-5" />}
                />
                <MetricCard
                  label="AC/DC"
                  value={flow ? formatPercent(flow.totalDc > 0 && flow.inverters.every(item => item.hasAc && item.hasDc) ? (flow.totalAc / flow.totalDc) * 100 : null) : ' '}
                  subvalue="Santral oranı"
                  state={flow?.state ?? 'warn'}
                  icon={<Zap className="h-5 w-5" />}
                />
                <MetricCard
                  label="Alarm"
                  value={`${warningItems.length}`}
                  subvalue="Kontrol gerektiren parça"
                  state={warningItems.length > 0 ? 'warn' : 'ok'}
                  icon={<Siren className="h-5 w-5" />}
                />
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_80px_-44px_rgba(15,23,42,0.35)]">
              <div className="flex items-center gap-2">
                <TriangleAlert className="h-4 w-4 text-amber-500" />
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Öncelikli Uyarılar</h3>
              </div>

              <div className="mt-3 space-y-2.5">
                {warningItems.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-5 text-sm font-medium text-slate-500">
                    Uyarı yok. Tüm parçalar sağlıklı görünüyor.
                  </div>
                ) : warningItems.map(({ inverter, stringNode }) => {
                  const tone = stateTone(stringNode.state === 'fault' || inverter.state === 'fault' ? 'fault' : 'warn');
                  return (
                    <div
                      key={`${inverter.id}-${stringNode.number}`}
                      className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
                    >
                      <div className="space-y-1">
                        <div className="text-[13px] font-black text-slate-900">{`${inverter.name} • S-${String(stringNode.number).padStart(2, '0')}`}</div>
                        <div className="text-[11px] font-semibold text-slate-500">
                          {stringReading(inverter.id, stringNode, 'voltage', 'V')} · {stringReading(inverter.id, stringNode, 'current', 'A')} · {stringReading(inverter.id, stringNode, 'dcPower', 'kW')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/inverters/${inverter.id}/string/${stringNode.number}`)}
                        className="rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em]"
                        style={{ color: tone.accent, background: tone.fill, border: `1px solid ${tone.stroke}30` }}
                      >
                        Detay
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_80px_-44px_rgba(15,23,42,0.35)]">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-sky-500" />
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Santral Detayı</h3>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2.5 text-sm">
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">Günlük</div>
                  <div className="mt-1 text-[13px] font-bold text-slate-900">{flow?.cards?.dailyTotalPower ?? ''}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">Haftalık</div>
                  <div className="mt-1 text-[13px] font-bold text-slate-900">{flow?.cards?.weeklyTotalPower ?? ''}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">Aylık</div>
                  <div className="mt-1 text-[13px] font-bold text-slate-900">{flow?.cards?.monthlyTotalPower ?? ''}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">Yıllık</div>
                  <div className="mt-1 text-[13px] font-bold text-slate-900">{flow?.cards?.yearlyTotalPower ?? ''}</div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2.5 text-sm">
                <div className="rounded-xl bg-sky-50 p-2.5">
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-sky-600">Panel Tipi</div>
                  <div className="mt-1 text-[13px] font-bold text-slate-900">
                    {(flow?.panelSummary ?? []).map((panel) => [panel.solar_module_brand, panel.solar_module_model].filter(Boolean).join(' / ')).join(', ')}
                  </div>
                </div>
                <div className="rounded-xl bg-sky-50 p-2.5">
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-sky-600">Panel Sayısı</div>
                  <div className="mt-1 text-[13px] font-bold text-slate-900">{(flow?.panelSummary ?? []).reduce((total, panel) => total + (panel.module_quantity ?? 0), 0) || ''}</div>
                </div>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_24px_80px_-44px_rgba(15,23,42,0.35)]">
              <div className="flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-slate-500" />
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">İnverter Listesi</h3>
              </div>
              <div className="mt-3 space-y-2.5">
                {flow?.inverters.map((inverter) => {
                  const tone = stateTone(inverter.state);
                  return (
                    <button
                      key={inverter.id}
                      type="button"
                      onClick={() => navigate(`/dashboard/inverters/${inverter.id}`)}
                      className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition hover:bg-slate-100"
                    >
                      <div>
                        <div className="text-[13px] font-black text-slate-900">{inverter.name}</div>
                        <div className="text-[11px] font-semibold text-slate-500">
                          <TrendValue value={inverter.hasAc ? inverter.acPower : null} previous={previousInverters.get(inverter.id)?.hasAc ? previousInverters.get(inverter.id)?.acPower : null} unit="kW" /> AC · <TrendValue value={inverter.hasDc ? inverter.dcPower : null} previous={previousInverters.get(inverter.id)?.hasDc ? previousInverters.get(inverter.id)?.dcPower : null} unit="kW" /> DC<div>{flowMode === 'history' ? 'O ana kadar' : 'Günlük üretim'}: {inverter.dailyEnergyKwh == null ? '—' : formatValue(inverter.dailyEnergyKwh, 'kWh', 2)}</div>
                        </div>
                      </div>
                      <span
                        className="rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em]"
                        style={{ color: tone.accent, background: tone.fill, border: `1px solid ${tone.stroke}30` }}
                      >
                        {inverter.state === 'fault' ? 'Arıza' : inverter.state === 'warn' ? 'Sapma' : 'OK'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
          ) : null}
        </div>
      </div>
    </div>
  );
}
